import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Search, Loader2, FileSearch } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { api, type SearchHit } from '@/lib/api'
import { cn } from '@/lib/utils'

const TOP_K = 5
const DEBOUNCE_MS = 200

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
}

// ts_headline emits <mark>…</mark> around matched tokens. We split on those
// segments and render <mark> as a React node so we never inject raw HTML.
function HighlightedSnippet({ html }: { html: string }) {
  const parts = useMemo(() => html.split(/(<mark>[\s\S]*?<\/mark>)/g), [html])
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('<mark>') && part.endsWith('</mark>')) {
          const inner = part.slice('<mark>'.length, -'</mark>'.length)
          return (
            <mark
              key={i}
              className="rounded-sm bg-primary/20 px-0.5 text-foreground"
            >
              {inner}
            </mark>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function ResultRow({
  hit,
  active,
  onSelect,
  onHover,
}: {
  hit: SearchHit
  active: boolean
  onSelect: () => void
  onHover: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHover}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg p-2.5 text-left transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent/60',
      )}
    >
      <img
        src={
          hit.thumbnailUrl ||
          `https://img.youtube.com/vi/${hit.youtubeVideoId}/mqdefault.jpg`
        }
        alt=""
        loading="lazy"
        className="h-14 w-24 shrink-0 rounded-md bg-muted object-cover"
      />
      <div className="min-w-0 flex-1">
        <h4 className="line-clamp-2 text-sm font-medium leading-snug">
          {hit.title}
        </h4>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {hit.channelName} ·{' '}
          {formatDistanceToNow(new Date(hit.publishedAt), { addSuffix: true })}
        </p>
        {hit.summarySnippet && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            <HighlightedSnippet html={hit.summarySnippet} />
          </p>
        )}
      </div>
    </button>
  )
}

function SearchModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchHit[]>([])
  const [loading, setLoading] = useState(false)
  // True after the instant keyword wave has rendered but the semantic wave is
  // still in flight — drives the "finding more" indicator.
  const [loadingMore, setLoadingMore] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  // Tracks the latest in-flight request so out-of-order responses (slow
  // network on an early keystroke completing after a fresh one) can't
  // overwrite the newer result set.
  const reqIdRef = useRef(0)
  // reqId for which the semantic (hybrid) wave has landed — so the keyword
  // wave can't overwrite the better fused results if it resolves later.
  const hybridDoneRef = useRef(0)

  // Reset transient state every time the modal opens. We don't preserve
  // the prior query — fresh palette on each invocation is the standard
  // command-palette pattern (Linear, GitHub, VS Code).
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setActiveIdx(0)
      setLoading(false)
      setLoadingMore(false)
    }
  }, [open])

  // Debounced two-wave search. Wave 1 (mode=keyword) skips the slow query
  // embedding and returns instantly for immediate feedback; wave 2
  // (mode=hybrid) folds in semantic matches once the embedding resolves and
  // replaces the list. Empty input clears results immediately.
  useEffect(() => {
    if (!open) return
    const trimmed = query.trim()
    if (!trimmed) {
      setResults([])
      setLoading(false)
      setLoadingMore(false)
      return
    }
    setLoading(true)
    setLoadingMore(false)
    const myReqId = ++reqIdRef.current
    const t = setTimeout(() => {
      // Tracks whether the keyword wave produced results for THIS request, so
      // the semantic wave's error handler knows whether there's anything worth
      // keeping (vs. a total failure that should clear to "no matches").
      let wave1Ok = false

      // Wave 1 — instant keyword results.
      api
        .search(trimmed, TOP_K, 'keyword')
        .then((res) => {
          // Ignore if a newer query started, or the semantic wave already won.
          if (reqIdRef.current !== myReqId || hybridDoneRef.current === myReqId) return
          wave1Ok = true
          setResults(res.results)
          setActiveIdx(0)
          setLoading(false)
          setLoadingMore(true)
        })
        .catch((err) => {
          if (reqIdRef.current !== myReqId) return
          console.error('[search] keyword wave', err)
        })

      // Wave 2 — semantic + fused results (waits on the query embedding).
      api
        .search(trimmed, TOP_K, 'hybrid')
        .then((res) => {
          if (reqIdRef.current !== myReqId) return
          hybridDoneRef.current = myReqId
          setResults(res.results)
          setLoading(false)
          setLoadingMore(false)
        })
        .catch((err) => {
          if (reqIdRef.current !== myReqId) return
          console.error('[search] semantic wave', err)
          // If the keyword wave already rendered, keep it; otherwise this is a
          // total failure — clear so we don't strand stale results.
          if (!wave1Ok) setResults([])
          setLoading(false)
          setLoadingMore(false)
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query, open])

  const selectResult = useCallback(
    (hit: SearchHit) => {
      onOpenChange(false)
      navigate(`/videos/${hit.videoId}`)
    },
    [navigate, onOpenChange],
  )

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = results[activeIdx]
      if (hit) selectResult(hit)
    }
  }

  const trimmed = query.trim()

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            inputRef.current?.focus()
          }}
          className="fixed left-1/2 top-[20vh] z-50 w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-background shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">
            Search summaries
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search across the titles, key points, and summaries of videos in your library.
          </DialogPrimitive.Description>

          <div className="flex items-center gap-3 border-b px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search by topic or idea, not just keywords…"
              className="h-12 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {(loading || loadingMore) && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            )}
            <kbd className="hidden shrink-0 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
              esc
            </kbd>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-2">
            {!trimmed ? (
              <EmptyHint />
            ) : results.length === 0 ? (
              // While either wave is still in flight, keep showing skeletons —
              // don't flash "no matches" before the semantic wave has a chance.
              loading || loadingMore ? (
                <SkeletonRows />
              ) : (
                <NoMatches query={trimmed} />
              )
            ) : (
              <>
                <SectionedResults
                  results={results}
                  activeIdx={activeIdx}
                  onSelect={selectResult}
                  onHover={setActiveIdx}
                />
                {loadingMore && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Finding semantically related results…
                  </div>
                )}
              </>
            )}
          </div>

          {results.length > 0 && (
            <div className="flex items-center justify-between border-t px-4 py-2 text-[11px] text-muted-foreground">
              <span>{results.length} result{results.length === 1 ? '' : 's'}</span>
              <span className="hidden items-center gap-1 sm:flex">
                <kbd className="rounded border bg-muted px-1 py-0.5">↑↓</kbd>
                navigate
                <kbd className="ml-2 rounded border bg-muted px-1 py-0.5">↵</kbd>
                open
              </span>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// Backend returns hits ordered with isFollowed=true first, so we slice at
// the section boundary and preserve the global index per hit for keyboard
// navigation (the flat `activeIdx` traverses both sections seamlessly).
function SectionedResults({
  results,
  activeIdx,
  onSelect,
  onHover,
}: {
  results: SearchHit[]
  activeIdx: number
  onSelect: (hit: SearchHit) => void
  onHover: (idx: number) => void
}) {
  const splitIdx = results.findIndex((h) => !h.isFollowed)
  const followed = splitIdx === -1 ? results : results.slice(0, splitIdx)
  const discovery = splitIdx === -1 ? [] : results.slice(splitIdx)

  return (
    <div className="space-y-2">
      {followed.length > 0 && (
        <div>
          <SectionHeader label="Your library" />
          <div className="space-y-1">
            {followed.map((hit, i) => (
              <ResultRow
                key={hit.videoId}
                hit={hit}
                active={i === activeIdx}
                onSelect={() => onSelect(hit)}
                onHover={() => onHover(i)}
              />
            ))}
          </div>
        </div>
      )}
      {discovery.length > 0 && (
        <div>
          <SectionHeader label="Across Rundown" />
          <div className="space-y-1">
            {discovery.map((hit, i) => {
              const globalIdx = followed.length + i
              return (
                <ResultRow
                  key={hit.videoId}
                  hit={hit}
                  active={globalIdx === activeIdx}
                  onSelect={() => onSelect(hit)}
                  onHover={() => onHover(globalIdx)}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  )
}

function EmptyHint() {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground">
      <FileSearch className="h-6 w-6 opacity-60" />
      <p>Search across every summary on Rundown.</p>
      <p className="text-xs">
        Your library is surfaced first; the rest of Rundown follows.
      </p>
    </div>
  )
}

function NoMatches({ query }: { query: string }) {
  return (
    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
      No matches for{' '}
      <span className="font-medium text-foreground">"{query}"</span>.
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 p-2.5">
          <div className="h-14 w-24 shrink-0 animate-pulse rounded-md bg-muted" />
          <div className="flex-1 space-y-1.5 pt-1">
            <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function SearchTrigger({ className }: { className?: string }) {
  const [open, setOpen] = useState(false)
  const mac = isMac()

  // Global Cmd+K (or Ctrl+K) handler. Toggles the modal so a second press
  // closes it — matches the convention used by Linear, GitHub, VS Code.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm text-muted-foreground shadow-sm transition-colors hover:border-primary/30 hover:text-foreground',
          className,
        )}
        aria-label="Open search (Cmd+K)"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search summaries…</span>
        <kbd className="hidden items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium sm:inline-flex">
          {mac ? '⌘' : 'Ctrl'}K
        </kbd>
      </button>
      <SearchModal open={open} onOpenChange={setOpen} />
    </>
  )
}
